import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Input from '@material-ui/core/Input';
import FilledInput from '@material-ui/core/FilledInput/FilledInput';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import cx from 'classnames';
import withStyles from '@material-ui/core/styles/withStyles';
import styles from './styles';
import defaultChipRenderer from './ChipRenderer';
import { ChipInputProps } from './types';

const variantComponent: Record<string, any> = {
  standard: Input,
  filled: FilledInput,
  outlined: OutlinedInput,
};

const keyCodes = {
  BACKSPACE: 8,
  DELETE: 46,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
};

const ChipInput: React.FC<ChipInputProps> = (props) => {
  const {
    allowDuplicates = false,
    alwaysShowPlaceholder,
    blurBehavior = 'clear',
    chipRenderer = defaultChipRenderer,
    classes = {} as Record<string, string>,
    className,
    clearInputValueOnChange = false,
    dataSource,
    dataSourceConfig,
    defaultValue,
    delayBeforeAdd = false,
    disabled,
    disableUnderline = false,
    error,
    filter,
    FormHelperTextProps,
    fullWidth,
    fullWidthInput,
    helperText,
    id,
    InputProps = {},
    inputRef,
    InputLabelProps = {},
    inputValue,
    label,
    newChipKeyCodes = [13],
    newChipKeys = ['Enter'],
    onBeforeAdd,
    onAdd,
    onBlur,
    onDelete,
    onChange,
    onFocus,
    onKeyDown,
    onKeyPress,
    onKeyUp,
    onUpdateInput,
    placeholder,
    readOnly,
    required = true,
    rootRef,
    value,
    variant = 'standard',
    validInput = () => true,
    allowCommaSplit = false,
    ...other
  } = props;

  // state refs
  const [chips, setChips] = useState<any[]>(() => defaultValue || []);
  const [focusedChip, setFocusedChip] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [chipsUpdated, setChipsUpdated] = useState(false);
  const [localInputValue, setLocalInputValue] = useState('');
  const [prevPropsValue, setPrevPropsValue] = useState<any[]>(value || []);

  const labelRef = useRef<HTMLLabelElement | null>(null);
  const input = useRef<any>(null);
  const actualInput = useRef<HTMLInputElement | null>(null);
  const [labelNode, setLabelNode] = useState<HTMLElement | null>(null);
  const inputBlurTimeout = useRef<NodeJS.Timeout>();
  const keyPressed = useRef(false);
  const preventChipCreation = useRef(false);

  // componentDidMount (outlined label node)
  useEffect(() => {
    if (variant === 'outlined') {
      const node = ReactDOM.findDOMNode(labelRef.current) as HTMLElement | null;
      setLabelNode(node);
    }
    return () => {
      if (inputBlurTimeout.current) clearTimeout(inputBlurTimeout.current);
    };
  }, [variant]);

  // getDerivedStateFromProps equivalent
  useEffect(() => {
    if (value && value.length !== prevPropsValue.length) {
      setPrevPropsValue(value);
      if (clearInputValueOnChange) setLocalInputValue('');
    }
  }, [value, prevPropsValue.length, clearInputValueOnChange]);

  useEffect(() => {
    if (!chipsUpdated && defaultValue) setChips(defaultValue);
  }, [chipsUpdated, defaultValue]);

  useEffect(() => {
    if (disabled) setFocusedChip(null);
  }, [disabled]);

  // helpers
  const clearInput = useCallback(() => setLocalInputValue(''), []);
  const updateInput = useCallback((v: string) => setLocalInputValue(v), []);

  const updateChips = useCallback(
    (
      nextChips: any[],
      additionalUpdates: Partial<{ focusedChip: number | null }> = {}
    ) => {
      setChips(nextChips);
      setChipsUpdated(true);
      if (typeof additionalUpdates.focusedChip !== 'undefined') {
        setFocusedChip(additionalUpdates.focusedChip ?? null);
      }
      onChange && onChange(nextChips);
    },
    [onChange]
  );

  const handleAddChipsBulk = useCallback(
    (chipsArray: any[], options?: { clearInputOnFail?: boolean }) => {
      let next = [...(value || chips)];
      let addedAtLeastOne = false;

      chipsArray.forEach((c) => {
        let chip = c;
        if (!chip || (onBeforeAdd && !onBeforeAdd(chip))) {
          if (options?.clearInputOnFail) clearInput();
          return;
        }

        if (dataSourceConfig) {
          if (typeof chip === 'string') {
            chip = {
              [dataSourceConfig.text]: chip,
              [dataSourceConfig.value]: chip,
            };
          }
          if (
            allowDuplicates ||
            !next.some(
              (x: any) =>
                x[dataSourceConfig.value] === chip[dataSourceConfig.value]
            )
          ) {
            next.push(chip);
            addedAtLeastOne = true;
          }
        } else {
          if ((chip as string).trim().length > 0) {
            if (allowDuplicates || !next.includes(chip)) {
              next.push(chip);
              addedAtLeastOne = true;
            }
          }
        }
      });

      clearInput();

      if (!value) {
        updateChips(next);
      } else if (onAdd) {
        chipsArray.forEach((c) => onAdd(c));
      }

      return addedAtLeastOne;
    },
    [
      allowDuplicates,
      dataSourceConfig,
      onAdd,
      onBeforeAdd,
      updateChips,
      value,
      chips,
      clearInput,
    ]
  );

  const handleAddChip = useCallback(
    (chip: any, options?: { clearInputOnFail?: boolean }) => {
      if (allowCommaSplit && typeof chip === 'string' && chip.includes(',')) {
        const chipsToAdd = chip
          .split(',')
          .map((c: string) => c.trim())
          .filter(Boolean);
        return handleAddChipsBulk(chipsToAdd, options);
      }

      if (onBeforeAdd && !onBeforeAdd(chip)) {
        preventChipCreation.current = true;
        if (options != null && options.clearInputOnFail) {
          clearInput();
        }
        return false;
      }

      clearInput();
      const current = value || chips;

      if (dataSourceConfig) {
        if (typeof chip === 'string') {
          chip = {
            [dataSourceConfig.text]: chip,
            [dataSourceConfig.value]: chip,
          };
        }

        if (
          allowDuplicates ||
          !current.some(
            (c: any) =>
              c[dataSourceConfig.value] === chip[dataSourceConfig.value]
          )
        ) {
          if (value && onAdd) {
            onAdd(chip);
          } else {
            updateChips([...chips, chip]);
          }
        }
        return true;
      }

      if ((chip as string).trim().length > 0) {
        if (allowDuplicates || current.indexOf(chip) === -1) {
          if (value && onAdd) {
            onAdd(chip);
          } else {
            updateChips([...chips, chip]);
          }
        }
        return true;
      }

      return false;
    },
    [
      allowCommaSplit,
      dataSourceConfig,
      allowDuplicates,
      value,
      onAdd,
      onBeforeAdd,
      updateChips,
      chips,
      clearInput,
      handleAddChipsBulk,
    ]
  );

  const handleDeleteChip = useCallback(
    (chip: any, i: number) => {
      if (!value) {
        const next = chips.slice();
        const changed = next.splice(i, 1);
        if (changed) {
          let nextFocused = focusedChip;
          if (focusedChip === i) nextFocused = null;
          else if ((focusedChip ?? -1) > i)
            nextFocused = (focusedChip as number) - 1;
          updateChips(next, { focusedChip: nextFocused });
        }
      } else if (onDelete) {
        onDelete(chip, i);
      }
    },
    [value, chips, focusedChip, onDelete, updateChips]
  );

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur && onBlur(event);
      setIsFocused(false);
      if (focusedChip != null) setFocusedChip(null);
      const v = event.target.value;
      let addChipOptions: any;
      switch (blurBehavior) {
        case 'add-or-clear':
          addChipOptions = { clearInputOnFail: true };
        // falls through
        case 'add':
          if (delayBeforeAdd) {
            const numChipsBefore = (value || chips).length;
            inputBlurTimeout.current = setTimeout(() => {
              const numChipsAfter = (value || chips).length;
              if (numChipsBefore === numChipsAfter) {
                handleAddChip(v, addChipOptions);
              } else {
                clearInput();
              }
            }, 150);
          } else {
            handleAddChip(v, addChipOptions);
          }
          break;
        case 'clear':
          clearInput();
          break;
        default:
          break;
      }
    },
    [
      onBlur,
      blurBehavior,
      delayBeforeAdd,
      value,
      chips,
      handleAddChip,
      clearInput,
      focusedChip,
    ]
  );

  const handleInputFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus && onFocus(event);
    },
    [onFocus]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const fc = focusedChip;
      keyPressed.current = false;
      preventChipCreation.current = false;
      if (onKeyDown) {
        onKeyDown(event);
        if (event.isDefaultPrevented && event.isDefaultPrevented()) return;
      }
      const chipsNow = value || chips;
      if (
        newChipKeyCodes.indexOf(event.keyCode) >= 0 ||
        newChipKeys.indexOf(event.key) >= 0
      ) {
        // create a chip from current input element's value
        const target = event.target as HTMLInputElement;
        const result = handleAddChip(target.value);
        if (result !== false) {
          event.preventDefault();
        }
        return;
      }

      switch (event.keyCode) {
        case keyCodes.BACKSPACE:
          if ((event.target as HTMLInputElement).value === '') {
            if (fc != null) {
              handleDeleteChip(chipsNow[fc], fc);
              if (fc > 0) setFocusedChip(fc - 1);
            } else {
              setFocusedChip(chipsNow.length - 1);
            }
          }
          break;
        case keyCodes.DELETE:
          if ((event.target as HTMLInputElement).value === '' && fc != null) {
            handleDeleteChip(chipsNow[fc], fc);
            if (fc <= chipsNow.length - 1) setFocusedChip(fc);
          }
          break;
        case keyCodes.LEFT_ARROW:
        case keyCodes.RIGHT_ARROW:
          break;
        default:
          setFocusedChip(null);
          break;
      }
    },
    [
      focusedChip,
      onKeyDown,
      newChipKeyCodes,
      newChipKeys,
      handleAddChip,
      value,
      chips,
      handleDeleteChip,
    ]
  );

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        !preventChipCreation.current &&
        (newChipKeyCodes.indexOf(event.keyCode) >= 0 ||
          newChipKeys.indexOf(event.key) >= 0) &&
        keyPressed.current
      ) {
        clearInput();
      } else {
        updateInput((event.target as HTMLInputElement).value);
      }
      onKeyUp && onKeyUp(event);
    },
    [newChipKeyCodes, newChipKeys, onKeyUp, updateInput]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      keyPressed.current = true;
      onKeyPress && onKeyPress(event);
    },
    [onKeyPress]
  );

  const handleUpdateInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (inputValue == null) updateInput(e.target.value);
      onUpdateInput && onUpdateInput(e);
    },
    [inputValue, onUpdateInput, updateInput]
  );

  const setActualInputRef = useCallback(
    (ref: HTMLInputElement | null) => {
      actualInput.current = ref;
      if (inputRef) inputRef(ref);
    },
    [inputRef]
  );

  // render
  const chipsToUse = value || chips;
  const actualInputValue = inputValue != null ? inputValue : localInputValue;
  const hasInput = (value?.length || actualInputValue.length) > 0;

  const shrinkFloatingLabel =
    InputLabelProps && typeof InputLabelProps.shrink !== 'undefined'
      ? InputLabelProps.shrink
      : label != null && (hasInput || isFocused || chipsToUse.length > 0);

  const chipComponents = chipsToUse.map((chipItem, i) => {
    const v = dataSourceConfig ? chipItem[dataSourceConfig.value] : chipItem;
    return chipRenderer(
      {
        value: v,
        text: dataSourceConfig ? chipItem[dataSourceConfig.text] : chipItem,
        chip: chipItem,
        isDisabled: !!disabled,
        isReadOnly: readOnly,
        isFocused: focusedChip === i,
        handleClick: () => setFocusedChip(i),
        handleDelete: () => handleDeleteChip(chipItem, i),
        className: classes.chip,
        chipErrorClassName: classes.chipError,
        validInput: validInput!,
      },
      i
    );
  });

  const InputMore: Record<string, any> = {};
  if (variant === 'outlined') {
    InputMore.notched = shrinkFloatingLabel;
    InputMore.labelWidth =
      (shrinkFloatingLabel && labelNode && labelNode.offsetWidth) || 0;
  }

  if (variant !== 'standard') {
    InputMore.startAdornment = <>{chipComponents}</>;
  } else {
    InputProps.disableUnderline = true;
  }

  const InputComponent = variantComponent[variant];

  return (
    <FormControl
      ref={rootRef}
      fullWidth={fullWidth}
      className={cx(className, classes.root, {
        [classes.marginDense]: (other as any).margin === 'dense',
      })}
      error={error}
      required={required}
      onClick={() => actualInput.current?.focus()}
      disabled={disabled}
      variant={variant}
      {...other}
    >
      {label && (
        <InputLabel
          htmlFor={id}
          classes={{
            root: cx(classes[variant], classes.label),
            shrink: classes.labelShrink,
          }}
          shrink={shrinkFloatingLabel}
          focused={isFocused}
          variant={variant}
          ref={labelRef}
          required={required}
          {...InputLabelProps}
        >
          {label}
        </InputLabel>
      )}

      <div
        className={cx(classes[variant], classes.chipContainer, {
          [classes.focused]: isFocused,
          [classes.underline]: !disableUnderline && variant === 'standard',
          [classes.disabled]: disabled,
          [classes.labeled]: label != null,
          [classes.error]: error,
        })}
      >
        {variant === 'standard' && chipComponents}

        <InputComponent
          ref={input}
          classes={{
            input: cx(classes.input, classes[variant]),
            root: cx(classes.inputRoot, classes[variant]),
          }}
          id={id}
          value={actualInputValue}
          onChange={handleUpdateInput}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          inputRef={setActualInputRef}
          disabled={disabled}
          fullWidth={fullWidthInput}
          placeholder={
            (!hasInput && (shrinkFloatingLabel || label == null)) ||
            alwaysShowPlaceholder
              ? placeholder
              : null
          }
          readOnly={readOnly}
          {...InputProps}
          {...InputMore}
        />
      </div>

      {helperText && (
        <FormHelperText
          {...FormHelperTextProps}
          className={
            FormHelperTextProps
              ? cx(FormHelperTextProps.className, classes.helperText)
              : classes.helperText
          }
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default withStyles(styles as any, { name: 'WAMuiChipInput' })(ChipInput);
